import { useTranslation } from "react-i18next"

import type { PersonaScriptHookDto } from "@/features/persona-opportunities/api/persona-opportunity-scripts.api"

export interface PersonaSendHookVariation {
  scriptId: string
  title: string
  hooks: readonly PersonaScriptHookDto[]
}

export interface PersonaSendHookVariationSetProps {
  scripts: readonly PersonaSendHookVariation[]
}

/** Each script with alternates is sent as one line that the partner shoots once per hook. */
export function PersonaSendHookVariationSet({ scripts }: PersonaSendHookVariationSetProps) {
  const { t } = useTranslation()
  const withAlternates = scripts.filter((script) => script.hooks.length > 1)
  if (withAlternates.length === 0) return null

  return (
    <section
      aria-label={t("personaOpportunities.scripts.sendDialog.variationSetLabel", "Hook variations")}
      className="rounded-lg border border-border bg-muted/30 px-3 py-2.5"
    >
      <ul className="flex flex-col gap-3">
        {withAlternates.map((script) => {
          const hooks = [...script.hooks].sort((a, b) => a.position - b.position)
          return (
            <li key={script.scriptId} className="min-w-0">
              <div className="flex items-baseline justify-between gap-2">
                <p className="min-w-0 truncate text-xs font-medium text-foreground">{script.title}</p>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {t("personaOpportunities.scripts.hookCountBadge", { count: hooks.length })}
                </span>
              </div>
              <ol
                aria-label={t("personaOpportunities.scripts.sendDialog.variationSetHooksLabel", {
                  title: script.title,
                  defaultValue: "{{title}} hooks",
                })}
                className="mt-1 list-decimal space-y-0.5 pl-4 text-xs text-foreground/80"
              >
                {hooks.map((hook) => (
                  <li key={hook.hookKey} className="break-words">
                    {hook.voiceover}
                    {hook.position === 1 ? (
                      <span className="ml-1.5 text-muted-foreground">
                        {t("personaOpportunities.scripts.sendDialog.variationSetPrimary", "Primary")}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ol>
              {/* Reserved for a per-partner hook price and minimum; partners publish none yet. */}
              <div data-slot="partner-hook-pricing" aria-hidden="true" />
            </li>
          )
        })}
      </ul>
    </section>
  )
}
